import React, { useState } from "react";
import {
  Lightbulb,
  Settings,
  Star,
  Trophy,
  Leaf,
  Target,
  Eye,
  ShieldCheck,
  Globe,
  Award,
  CheckCircle2,
  Mail,
  Phone,
  GraduationCap,
  History,
  MapPin,
  User,
  Building2,
  Factory,
  Sprout,
  School,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { ComplianceSection } from "./ComplianceSection";
import { JourneySection } from "./JourneySection";

// Partner logos from public/assets/logo
const PARTNER_LOGOS: { name: string; file: string }[] = [
  { name: "Andhra Bank", file: "Andhra_bank.svg 1.webp" },
  { name: "Arihant Group", file: "Arihant-Group-Photoroom 1.webp" },
  { name: "Bharti Airtel", file: "Bharti_Airtel-Logo.wine 1.webp" },
  { name: "Dena Bank", file: "Dena_Bank_Logo 1.webp" },
  { name: "Havells", file: "Havells-Logo.wine 1.webp" },
  { name: "HDFC Bank", file: "HDFC_Bank_Logo.svg 1.webp" },
  {
    name: "Bajaj Electricals",
    file: "kisspng-logo-bajaj-electricals-portable-network-graphics-d-bajaj-electricals-getevangelized-1713903939746-Photoroom 1.webp",
  },
  { name: "Lodha", file: "Lodha---New-LOgo 1.webp" },
  { name: "Orient Electric", file: "Orient_Electric_logo.svg 1.webp" },
  {
    name: "Canara Bank",
    file: "png-clipart-canara-bank-logo-horizontal-bilingual-bank-logos-Photoroom 1.webp",
  },
  { name: "State Bank of India", file: "Sbi_logo.svg-Photoroom 1.webp" },
  { name: "Union Bank of India", file: "Union_Bank_of_India_Logo.svg 1.webp" },
  { name: "Vodafone", file: "Vodafone_2017_logo.svg 1.webp" },
  { name: "Partner", file: "4bc79d18780435.562cf2c77e290-Photoroom 1.webp" },
  { name: "Partner", file: "g742.webp" },
  { name: "Partner", file: "images 1.webp" },
  { name: "Partner", file: "images-Photoroom 1.webp" },
  { name: "Partner", file: "path0.webp" },
  { name: "Partner", file: "Untitled design 1.webp" },
  { name: "Partner", file: "Untitled design (1) 1.webp" },
  { name: "Partner", file: "Untitled design (2) 1.webp" },
  { name: "Partner", file: "Untitled design (2) 2.webp" },
  { name: "Partner", file: "Untitled design (3) 1.webp" },
  { name: "Partner", file: "Untitled design (3) 2.webp" },
  { name: "Partner", file: "Untitled design (4) 1.webp" },
  { name: "Partner", file: "Untitled design (5) 1.webp" },
  { name: "Partner", file: "Untitled design (6) 1.webp" },
  { name: "Partner", file: "Untitled design (7) 1.webp" },
  { name: "Partner", file: "Untitled design (8) 1.webp" },
  { name: "Partner", file: "Untitled design (9) 1.webp" },
  { name: "Partner", file: "Untitled design (10) 1.webp" },
  { name: "Partner", file: "Untitled design (11) 1.webp" },
];

const PartnerCard: React.FC<{ partner: { name: string; file: string } }> = ({
  partner,
}) => (
  <div className="flex items-center justify-center bg-white rounded-2xl flex-shrink-0 border border-black/5 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden w-[140px] h-[130px] md:w-[202px] md:h-[188px]">
    <div className="w-full h-full p-4 flex items-center justify-center">
      <img
        src={encodeURI(`/assets/logo/${partner.file}`)}
        alt={partner.name}
        loading="lazy"
        className="w-full h-full object-contain opacity-90 hover:opacity-100 transition-opacity"
        style={{ filter: "contrast(1.1) saturate(1.05)" }}
      />
    </div>
  </div>
);

// Phone numbers from Contact page (Hoshiarpur office) and Footer
const CONTACT_PHONES = [
  { label: "General enquiries", number: "+91 89680 99909", tel: "+918968099909" },
  { label: "General enquiries", number: "+91 7347012168", tel: "+917347012168" },
];

export const AboutPage: React.FC = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  return (
    <div className="pt-[5rem] bg-white min-h-screen">
      {/* 1 Profile Sidebar */}
      <section className="bg-[#FAFAFA] px-2 lg:px-12 2xl:px-12 py-10">
        <div className="container max-w-full lg:max-w-[70%] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 items-start">
            <div className="w-full lg:sticky lg:top-32 self-start shrink-0 order-2 lg:order-1 lg:row-span-2">
              <div className="bg-white rounded-3xl p-4 shadow-sm flex flex-col gap-4 border border-gray-100">
                {/* Profile Image Card */}
                <div className="relative rounded-3xl overflow-hidden group">
                  <img
                    src="/assets/logos/chairmen.webp"
                    alt="Naresh Kumar Tewari"
                    className="w-full h-[480px] object-cover grayscale brightness-90 contrast-110"
                  />
                  {/* Profile Info Overlay */}
                  <div className="absolute bottom-4 left-4 right-4 bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-white/40">
                    <h2 className="text-card-title font-open-sans-hebrew font-bold text-gray-900">
                      Naresh Kumar Tewari
                    </h2>
                    <p className="text-body text-gray-500 font-medium font-raleway">
                      Industrial Leader & Visionary
                    </p>
                  </div>
                </div>

                {/* Info List */}
                <div className="px-2 ">
                  <div className="flex items-center gap-4 text-gray-600 mb-1">
                    <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center bg-gray-50">
                      <GraduationCap size={16} />
                    </div>
                    <span className="text-body font-medium font-raleway">
                      Arts Graduate (1972)
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-gray-600 mb-1">
                    <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center bg-gray-50">
                      <History size={16} />
                    </div>
                    <span className="text-body font-medium font-raleway">
                      Industry Active Since 1985
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-gray-600">
                    <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center bg-gray-50">
                      <MapPin size={16} />
                    </div>
                    <span className="text-body font-medium font-raleway">
                      Punjab, India
                    </span>
                  </div>
                </div>

                {/* Action Buttons - same email/contact as Contact page */}
                <div className="grid grid-cols-2 gap-3 px-2 mb-2">
                  <a
                    href="mailto:support@cromaply.com"
                    className="flex items-center justify-center gap-2 py-3 px-4 bg-[#1a1a1a] text-white rounded-xl hover:bg-black transition-colors font-medium text-body font-raleway"
                  >
                    <Mail size={16} />
                    Email
                  </a>
                  <button
                    type="button"
                    onClick={() => setContactModalOpen(true)}
                    className="flex items-center justify-center gap-2 py-3 px-4 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-body font-raleway"
                  >
                    <Phone size={16} />
                    Contact
                  </button>
                </div>
              </div>
            </div>

            {/* Header Section */}
            <div className="space-y-4 px-4 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C9252B]"></div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600 font-raleway">
                  LEGACY OF 30+ YEARS
                </span>
              </div>

              <p
                className="text-3xl md:text-heading-large bg-gradient-to-r
                  from-[#e20016] from-[0%]
                  via-[#712f59] via-[40%]
                  to-[#005d9c] to-[100%] bg-clip-text text-transparent font-open-sans-hebrew font-bold max-w-3xl"
              >
                Constructing the future of the plywood industry through
                leadership & innovation.
              </p>

              <p className="text-gray-500 text-body leading-relaxed max-w-3xl font-raleway">
                I have dedicated my career to the plywood manufacturing industry
                for over three decades. My journey is marked by a deep
                commitment to the growth and development of this sector. I have
                not only excelled in my personal ventures but have also
                undertaken leadership roles in various industry associations to
                advocate for and educate fellow professionals.
              </p>
            </div>

            {/* Bento Grid Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 order-3 lg:col-start-2">
              {/* President Card - Full Width */}
              <div className="md:col-span-2 rounded-2xl border border-gray-100 bg-white p-6 sm:p-7 transition-all hover:shadow-md hover:bg-[#1a1a1a] hover:border-white/10 group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border bg-gray-50 border-gray-100 text-gray-700 transition-colors group-hover:bg-white/10 group-hover:border-white/10 group-hover:text-white">
                    <User size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 transition-colors group-hover:text-white">
                      President, NIPMA
                    </h3>
                    <p className="text-body mt-2 leading-relaxed text-gray-500 transition-colors group-hover:text-white/70 font-raleway">
                      Served as the unopposed president of the Northern India
                      Plywood Manufacturer Association for 15 years,
                      representing the interests of around 700 manufacturing
                      units.
                    </p>
                  </div>
                </div>
              </div>

              {/* IPIRTI Card */}
              <div className="rounded-2xl border border-gray-100 bg-white p-6 sm:p-7 transition-all hover:shadow-md hover:bg-[#1a1a1a] hover:border-white/10 group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border bg-gray-50 border-gray-100 text-gray-700 transition-colors group-hover:bg-white/10 group-hover:border-white/10 group-hover:text-white">
                    <Building2 size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 transition-colors group-hover:text-white">
                      IPIRTI Governing Body
                    </h3>
                  </div>
                </div>
                <div>
                  <p className="text-body mt-2 leading-relaxed text-gray-500 transition-colors group-hover:text-white/70 font-raleway">
                    Appointed by the Government of India to the prestigious
                    Indian Plywood Industry Research Training Institute,
                    reflecting deep expertise in the industry.
                  </p>
                </div>
              </div>

              {/* Founder Card */}
              <div className="rounded-2xl border border-gray-100 bg-white p-6 sm:p-7 transition-all hover:shadow-md hover:bg-[#1a1a1a] hover:border-white/10 group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border bg-gray-50 border-gray-100 text-gray-700 transition-colors group-hover:bg-white/10 group-hover:border-white/10 group-hover:text-white">
                    <Factory size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 transition-colors group-hover:text-white">
                      Founder, Virgo Panel Products
                    </h3>
                  </div>
                </div>
                <div>
                  <p className="text-body mt-2 leading-relaxed text-gray-500 transition-colors group-hover:text-white/70 font-raleway">
                    Established an ultramodern plywood manufacturing facility in
                    Hoshiarpur in 2015, contributing significantly to
                    Punjab&apos;s standing in the panel sector.
                  </p>
                </div>
              </div>

              {/* Plywood Park Card */}
              <div className="rounded-2xl border border-gray-100 bg-white p-6 sm:p-7 transition-all hover:shadow-md hover:bg-[#1a1a1a] hover:border-white/10 group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border bg-gray-50 border-gray-100 text-gray-700 transition-colors group-hover:bg-white/10 group-hover:border-white/10 group-hover:text-white">
                    <Sprout size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 transition-colors group-hover:text-white">
                      Plywood Park Project
                    </h3>
                  </div>
                </div>
                <div>
                  <p className="text-body mt-2 leading-relaxed text-gray-500 transition-colors group-hover:text-white/70 font-raleway">
                    Collaborating with Punjab Industries & Commerce to establish
                    a ₹100 crore Plywood Park, aiming to create 3,000 direct and
                    6,000 indirect jobs.
                  </p>
                </div>
              </div>

              {/* Chairman Card */}
              <div className="rounded-2xl border border-gray-100 bg-white p-6 sm:p-7 transition-all hover:shadow-md hover:bg-[#1a1a1a] hover:border-white/10 group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border bg-gray-50 border-gray-100 text-gray-700 transition-colors group-hover:bg-white/10 group-hover:border-white/10 group-hover:text-white">
                    <Award size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 transition-colors group-hover:text-white">
                      Chairman, AIPMA
                    </h3>
                  </div>
                </div>
                <div>
                  <p className="text-body mt-2 leading-relaxed text-gray-500 transition-colors group-hover:text-white/70 font-raleway">
                    Serving for over 25 years with the All India Plywood
                    Manufacturers&apos; Association, underscoring a lifelong
                    dedication to national industry growth.
                  </p>
                </div>
              </div>

              {/* Mohali Card - Full Width */}
              <div className="md:col-span-2 rounded-2xl border border-gray-100 bg-white p-6 sm:p-7 transition-all hover:shadow-md hover:bg-[#1a1a1a] hover:border-white/10 group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border bg-gray-50 border-gray-100 text-gray-700 transition-colors group-hover:bg-white/10 group-hover:border-white/10 group-hover:text-white">
                    <School size={20} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 transition-colors group-hover:text-white">
                      Establishment of IPIRTI Mohali
                    </h3>
                    <p className="text-body mt-2 leading-relaxed text-gray-500 transition-colors group-hover:text-white/70 font-raleway">
                      Played a crucial role with the Ministry of Environment &
                      Forests to set up this center, helping local industry
                      enhance product quality.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Dark Legacy Section - 70% width, well structured */}
      <section className="bg-black text-white mt-10 py-12 lg:py-16 xl:py-20">
        <div className="w-[90%] lg:w-[70%] max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 xl:gap-12 items-stretch">
            {/* Left: Image - height matches right column */}
            <div className="lg:w-[40%] flex-shrink-0 min-h-[260px] sm:min-h-[300px] lg:min-h-0 order-2 lg:order-1">
              <div className="w-full h-full min-h-[260px] sm:min-h-[300px] lg:min-h-0 rounded-xl lg:rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="/assets/images/about/core.webp"
                  alt="Croma Ply"
                  className="w-full h-full object-cover object-center opacity-90"
                />
              </div>
            </div>

            {/* Right: Content */}
            <div className="lg:w-[60%] flex flex-col gap-6 lg:gap-8 justify-between order-1 lg:order-2">
              {/* Intro paragraphs */}
              <div className="space-y-5">
                <p className="text-base sm:text-lg text-white/95 leading-relaxed font-raleway">
                  Croma Ply, the newly launched brand from the house of Virgo,
                  is built on innovation in design and quality. Located in
                  Hoshiarpur, the brand blends technology with research to
                  manufacture plywood that is high on quality, affordable, and
                  offers great value for customers.
                </p>
                <p className="text-base sm:text-lg text-white/95 leading-relaxed font-raleway">
                  By continuously experimenting and exploring ideas beyond the
                  ordinary, Croma Ply redefines possibilities in
                  next-generation plywood manufacturing.
                </p>
              </div>

              {/* Bullet list */}
              <ul className="space-y-3 text-body font-raleway">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-croma-blue shrink-0 mt-2" />
                  <p className="text-heading-sub text-white/90">
                    Legacy Built{" "}
                    <span className="font-bold bg-gradient-to-br from-croma-blue to-white bg-clip-text text-transparent">
                      Since 1985
                    </span>{" "}
                    With Proven Industry Leadership.
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-croma-blue shrink-0 mt-2" />
                  <p className="text-heading-sub text-white/90">
                    <span className="font-bold bg-gradient-to-br from-croma-blue to-white bg-clip-text text-transparent">
                      Technology-driven production
                    </span>{" "}
                    supported by research and testing.
                  </p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-croma-blue shrink-0 mt-2" />
                  <p className="text-heading-sub text-white/90">
                    Focus on{" "}
                    <span className="font-bold bg-gradient-to-br from-croma-blue to-white bg-clip-text text-transparent">
                      consistent quality
                    </span>
                    ,{" "}
                    <span className="font-bold bg-gradient-to-br from-croma-blue to-white bg-clip-text text-transparent">
                      affordability
                    </span>
                    , and{" "}
                    <span className="font-bold bg-gradient-to-br from-croma-blue to-white bg-clip-text text-transparent">
                      value
                    </span>{" "}
                    for customers.
                  </p>
                </li>
              </ul>

              {/* Mission & Vision cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 lg:gap-6 mt-2">
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 sm:p-6 hover:bg-white/[0.08] transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 text-white shrink-0">
                      <Target className="w-5 h-5" />
                    </span>
                    <h4 className="text-heading-sub font-open-sans-hebrew font-bold text-white">
                      Our Mission
                    </h4>
                  </div>
                  <p className="text-body text-white/80 font-raleway leading-relaxed">
                    Deliver innovative and technologically advanced plywood by
                    leveraging in-house Research & Development, EMI and
                    homologation testing labs, and strong manufacturing
                    processes to ensure consistent quality and timely delivery.
                  </p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 sm:p-6 hover:bg-white/[0.08] transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 text-white shrink-0">
                      <Eye className="w-5 h-5" />
                    </span>
                    <h4 className="text-heading-sub font-open-sans-hebrew font-bold text-white">
                      Our Vision
                    </h4>
                  </div>
                  <p className="text-body text-white/80 font-raleway leading-relaxed">
                    To redefine plywood possibilities by blending technology,
                    research, and innovation, while manufacturing high-quality
                    plywood that remains affordable and reliable for a growing
                    customer base.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Hero Section */}
      <section className="flex flex-col items-center px-6 md:px-8 lg:px-12 2xl:px-12 py-24">
        <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6 lg:gap-12 mb-8 lg:mb-16 w-full max-w-full lg:max-w-[70%] mx-auto">
          <div className="lg:w-[60%] flex flex-col">
            <div className="inline-flex items-center gap-2 mb-2 px-3 py-1.5 rounded-md">
              <div className="w-1.5 h-1.5 rounded-full bg-[#C9252B]"></div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600 font-raleway">
                ESTABLISHED IN 1985
              </span>
            </div>
            <p
              className="text-3xl md:text-heading-large bg-gradient-to-r
              from-[#e20016] from-[0%]
              via-[#712f59] via-[40%]
              to-[#005d9c] to-[100%] bg-clip-text text-transparent font-open-sans-hebrew font-bold"
            >
              Next Generation Engineered Wood Crafted With Modern Precision
            </p>
          </div>

          <div className="lg:w-1/3 pt-2 lg:pt-12 flex flex-col items-end justify-center">
            <p className="text-gray-500 text-body max-w-md font-raleway">
              Blending traditional craftsmanship with modern engineering, Croma
              Ply continues Virgo's legacy by delivering next-generation plywood
              designed for precision, durability, and trusted performance across
              diverse applications.
            </p>
          </div>
        </div>

        <div className="w-full max-w-full lg:max-w-[70%] mx-auto h-[300px] md:h-[500px] rounded-[32px] overflow-hidden relative border border-gray-200">
          <video
            src="/assets/video/coming-soon.webm"
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </section>

      {/* 4. Vision & Philosophy (Dark) - 70% width, 40% image / 60% content */}
      <section className="bg-black text-white overflow-hidden py-12 lg:py-16 xl:py-20">
        <div className="w-[90%] lg:w-[70%] max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 xl:gap-12 items-stretch">
            {/* Left: Image - height matches right column */}
            <div className="lg:w-[40%] flex-shrink-0 min-h-[260px] sm:min-h-[300px] lg:min-h-0 order-2 lg:order-1">
              <div className="w-full h-full min-h-[260px] sm:min-h-[300px] lg:min-h-0 rounded-xl lg:rounded-2xl overflow-hidden border border-white/10 flex items-center justify-center bg-white/5">
                <img
                  src="/assets/images/vision-and-philosophy.webp"
                  alt="Vision & Philosophy"
                  className="w-full h-full object-cover object-center opacity-90"
                />
              </div>
            </div>

            {/* Right: Content */}
            <div className="lg:w-[60%] flex flex-col justify-center order-1 lg:order-2 py-4 lg:py-0">
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 font-raleway mb-2 block">
                WHY CROMA PLY
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-heading-section font-open-sans-hebrew font-bold bg-gradient-to-r from-croma-blue to-white bg-clip-text text-transparent mb-4">
                Our Vision & Philosophy
              </h2>
              <p className="text-body text-sm lg:text-base text-white/90 font-raleway leading-relaxed mb-6 lg:mb-8">
                Croma Ply, the newly launched brand from the house of Virgo, is
                built on innovation in design and quality. Located in
                Hoshiarpur, the brand blends technology with research to
                manufacture plywood that is high on quality, affordable, and
                offers great value to its customer base.
              </p>

              <div className="space-y-3 sm:space-y-4">
                {[
                  {
                    icon: Lightbulb,
                    title: "Innovation in Design & Quality",
                    desc: "Driven by the desire to deliver innovative and technologically advanced plywood, supported by in-house Research & Development and testing facilities to ensure high-end manufacturing standards.",
                  },
                  {
                    icon: Settings,
                    title: "Technology-Led Manufacturing",
                    desc: "Advanced manufacturing processes combined with EMI and homologation testing labs ensure precision, consistency, and reliable plywood production at scale.",
                  },
                  {
                    icon: Globe,
                    title: "Research-Driven Development",
                    desc: "Continuous experimentation, testing, and improvement of existing product ranges to enhance performance, durability, and long-term reliability.",
                  },
                  {
                    icon: CheckCircle2,
                    title: "Built on Affordability & Value",
                    desc: "Strong focus on consistent product quality, timely distribution, and vertical integration to deliver dependable plywood solutions that provide real value to customers.",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex gap-3 border border-white/10 rounded-xl p-4 sm:p-5 hover:bg-white/[0.06] transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-white shrink-0">
                      <item.icon size={18} strokeWidth={1} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-heading-sub font-open-sans-hebrew font-bold bg-gradient-to-br from-croma-blue to-white bg-clip-text text-transparent mb-1 text-sm lg:text-base">
                        {item.title}
                      </h4>
                      <p className="text-body text-xs lg:text-sm text-white/80 leading-relaxed font-raleway">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Core Values */}
      <section className="bg-[#FAFAFA] py-12 md:py-24 px-6 md:px-12 2xl:px-12">
        <div className="container max-w-full lg:max-w-[70%] mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-end mb-8 md:mb-16">
            <p
              className="lg:w-3/4 text-3xl md:text-heading-large bg-gradient-to-r
              from-[#e20016] from-[0%]
              via-[#712f59] via-[40%]
              to-[#005d9c] to-[100%] bg-clip-text text-transparent font-open-sans-hebrew font-bold"
            >
              Core Values That Shape <br className="hidden md:block" /> Our
              Legacy And Future
            </p>
            <p className="lg:w-1/4 text-gray-500 text-body h-full pt-2 lg:pt-12 lg:mr-18 font-raleway">
              These values reflect our commitment to innovation, consistency,
              excellence, leadership, and responsible manufacturing across every
              product we create.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200 rounded-2xl overflow-hidden">
            {[
              {
                icon: Lightbulb,
                title: "Inspiring Innovation",
                desc: "Advanced plywood engineered through research",
              },
              {
                icon: Settings,
                title: "Installing Consistency",
                desc: "Uniform quality ensured through integration",
              },
              {
                icon: Star,
                title: "Imbibing Excellence",
                desc: "High standards backed by expert teams",
              },
              {
                icon: Trophy,
                title: "Leadership & Legacy",
                desc: "Four decades of proven industry leadership",
              },
              {
                icon: Leaf,
                title: "Responsible Manufacturing",
                desc: "Sustainable, precise, and zero-defect processes",
              },
            ].map((val, i) => (
              <div
                key={i}
                className="bg-white p-6 md:p-8 hover:bg-[#fafafa] transition-colors group h-full flex flex-col"
              >
                <div className="flex-1 flex flex-col justify-center">
                  <div className="w-12 h-12 flex items-center justify-center text-[#e20016] mb-6 transition-all">
                    <val.icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 mb-3">
                    {val.title}
                  </h3>
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <p className="text-body text-gray-500 font-raleway">
                    {val.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Trusted By */}
      <section className="py-12 md:py-24 bg-white overflow-hidden">
        <div className="max-w-full lg:max-w-[70%] mx-auto">
          <div className="mb-8 md:mb-16 px-6 lg:px-12 2xl:px-12">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-4 md:gap-8">
              <div className="max-w-4xl">
                <p
                  className="text-3xl md:text-heading-large mb-2 bg-gradient-to-r
                from-[#e20016] from-[0%]
                via-[#712f59] via-[40%]
                to-[#005d9c] to-[100%] bg-clip-text text-transparent font-open-sans-hebrew font-bold"
                >
                  Trusted By Leading Brands
                </p>
                <p
                  className="text-3xl md:text-heading-large mb-2 md:mb-8 bg-gradient-to-r
                from-[#e20016] from-[0%]
                via-[#712f59] via-[40%]
                to-[#005d9c] to-[100%] bg-clip-text text-transparent font-open-sans-hebrew font-bold"
                >
                  Across Every Industry
                </p>
              </div>
              <p className="text-gray-500 text-body max-w-xl lg:max-w-sm font-raleway">
                Discover the brands and partners who rely on Croma Ply for
                reliable quality, durability, and consistent performance.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-6 px-6 lg:px-12 2xl:px-12">
            {/* Row 1: Right to Left */}
            <div className="w-full overflow-hidden">
              <motion.div
                className="flex gap-6 pl-6"
                initial={{ x: 0 }}
                animate={{ x: "-50%" }}
                transition={{
                  repeat: Infinity,
                  ease: "linear",
                  duration: 90,
                }}
                style={{ width: "max-content" }}
              >
                {[...PARTNER_LOGOS, ...PARTNER_LOGOS, ...PARTNER_LOGOS].map(
                  (partner, i) => (
                    <PartnerCard key={`row1-${i}`} partner={partner} />
                  ),
                )}
              </motion.div>
            </div>

            {/* Row 2: Left to Right */}
            <div className="w-full overflow-hidden">
              <motion.div
                className="flex gap-6 pl-6"
                initial={{ x: "-50%" }}
                animate={{ x: "0%" }}
                transition={{
                  repeat: Infinity,
                  ease: "linear",
                  duration: 90,
                }}
                style={{ width: "max-content" }}
              >
                {[...PARTNER_LOGOS, ...PARTNER_LOGOS, ...PARTNER_LOGOS].map(
                  (partner, i) => (
                    <PartnerCard key={`row2-${i}`} partner={partner} />
                  ),
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Journey Timeline - same as HomePage */}
      <JourneySection />

      {/* 8. Precision Manufacturing */}
      {/* <ScrollReveal delay={0.1}>
        <section className="py-12 md:py-24 px-6 lg:px-12 bg-white">
          <div className="">
            <div className="text-center mb-8 md:mb-16 space-y-4">
              <p
                className="max-w-4xl mx-auto text-3xl md:text-heading-large bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent leading-tight md:leading-[3.5rem] font-open-sans-hebrew font-bold"
              >
                Precision-Engineered Manufacturing Delivering Consistent Quality
                Every Day
              </p>
              <p className="text-gray-500 text-body max-w-2xl mx-auto">
                Our advanced Hoshiarpur facility uses calibrated machinery,
                strict quality checks, and skilled engineering to produce
                reliable, high-performance plywood at scale.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-3/5 h-[300px] md:h-[500px] rounded-[24px] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1610557892470-55d9e80c0bce?q=80&w=2339&auto=format&fit=crop"
                  className="w-full h-full object-cover"
                  alt="Factory Interior"
                />
              </div>
              <div className="lg:w-2/5 flex flex-col gap-8">
                <div className="h-[200px] md:h-[300px] rounded-[24px] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2400&auto=format&fit=crop"
                    className="w-full h-full object-cover"
                    alt="Machinery"
                  />
                </div>
                <p className="text-body text-gray-600 p-4 bg-[#FAFAFA] rounded-2xl border border-gray-100">
                  Croma Ply's state-of-the-art Hoshiarpur facility integrates
                  advanced machinery, calibrated processing, in-house R&D, and
                  strict quality checks to ensure each sheet delivers consistent
                  strength, superior finish, and long-lasting performance across
                  every application.
                </p>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal> */}

      {/* 9. Certifications */}
      <section className="py-12 md:py-20 px-6 lg:px-12 bg-[#FAFAFA]">
        <div className="max-w-full lg:max-w-[70%] mx-auto">
          {/* Heading Section */}
          <div className="text-center mb-8 md:mb-16">
            <p
              className="max-w-4xl mx-auto py-2 text-3xl md:text-heading-large bg-gradient-to-r
            from-[#e20016] from-[0%]
            via-[#712f59] via-[40%]
            to-[#005d9c] to-[100%] bg-clip-text text-transparent leading-tight md:leading-[3.5rem] font-open-sans-hebrew font-bold"
            >
              A Journey Crafted Through Experience, Innovation, And Progress
            </p>
            <p className="text-gray-500 text-body mt-4 max-w-xl mx-auto font-raleway">
              From our 1985 beginning to today's advanced facility, our journey
              reflects industry leadership, global exposure, national
              recognition, and continuous innovation.
            </p>
          </div>

          {/* Compliance Section */}
          <div className="mb-16">
            <ComplianceSection
              items={[
                {
                  code: "IS 303",
                  description:
                    "BIS Specification for moisture resistant plywood & Boiling water resistance plywood",
                },
                {
                  code: "IS 710",
                  description:
                    "BIS Specification for Boiling Water Proof grade plywood.",
                },
                {
                  code: "IS 1659",
                  description: "BIS Specification for Block Boards.",
                },
                {
                  code: "IS 2202 (Part-1)",
                  description: "BIS Specification of Flush Door.",
                },
                {
                  code: "IS 5509",
                  description: "BIS Specification for fire Retardant Plywood.",
                },
                {
                  code: "ISO 9001 2015",
                  description:
                    "Quality Management System for meeting customers Expectations & developing customer satisfaction.",
                },
                {
                  code: "ISO 9001 2015",
                  description: "Environmental Management System.",
                },
                {
                  code: "MSME ZED Bronze Certification",
                  description:
                    "MSME Sustainable (ZED) Certification Scheme awarded to Virgo Panel Product for Zero Defect – Zero Effect manufacturing practices.",
                },
              ]}
            />
          </div>

          {/* Certification Cards */}
          <div className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="relative bg-white p-8 rounded-2xl border border-gray-100 flex flex-col items-start overflow-hidden group hover:shadow-md transition-all">
                <div className="absolute -top-20 -right-10 rounded-full bg-blue-50/80 h-[10rem] w-[10rem]"></div>
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 mb-6">
                  <ShieldCheck size={24} />
                </div>
                <span className="absolute top-8 right-8 text-[10px] font-bold text-blue-500 uppercase tracking-widest font-raleway">
                  CERTIFIED
                </span>
                <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 mb-2">
                  BIS Certified Standards
                </h3>
                <p className="text-body text-gray-500 mb-6 font-raleway">
                  Ensuring trusted performance through strict national
                  certifications.
                </p>
                <ul className="space-y-2 mt-auto">
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>{" "}
                    National Quality Assurance
                  </li>
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>{" "}
                    Regulatory Compliance
                  </li>
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>{" "}
                    Consumer Safety Standards
                  </li>
                </ul>
              </div>

              <div className="relative bg-white p-8 rounded-2xl border border-gray-100 flex flex-col items-start overflow-hidden group hover:shadow-md transition-all">
                <div className="absolute -top-20 -right-10 rounded-full bg-green-50/80 h-[10rem] w-[10rem]"></div>
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center text-green-600 mb-6">
                  <Globe size={24} />
                </div>
                <span className="absolute top-8 right-8 text-[10px] font-bold text-green-500 uppercase tracking-widest font-raleway">
                  ISO 9001
                </span>
                <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 mb-2">
                  ISO Quality Management
                </h3>
                <p className="text-body text-gray-500 mb-6 font-raleway">
                  Global quality systems guaranteeing consistent, reliable
                  manufacturing.
                </p>
                <ul className="space-y-2 mt-auto">
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>{" "}
                    International Standards
                  </li>
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>{" "}
                    Process Optimization
                  </li>
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>{" "}
                    Continuous Improvement
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-gray-100 flex flex-col items-start relative overflow-hidden group hover:shadow-md transition-all">
                <div className="absolute -top-20 -right-10 rounded-full bg-orange-50/80 h-[10rem] w-[10rem]"></div>
                <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center text-orange-600 mb-6">
                  <Award size={24} />
                </div>
                <span className="absolute top-8 right-8 text-[10px] font-bold text-orange-500 uppercase tracking-widest font-raleway">
                  BRONZE
                </span>
                <h3 className="text-card-title font-open-sans-hebrew font-bold text-gray-900 mb-2">
                  ZED Bronze Accreditation
                </h3>
                <p className="text-body text-gray-500 mb-6 font-raleway">
                  Eco-responsible processes built for zero-defect excellence.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>{" "}
                    Zero Waste Manufacturing
                  </li>
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>{" "}
                    Energy Efficiency
                  </li>
                  <li className="text-body text-gray-500 flex items-center gap-2 font-raleway">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>{" "}
                    Sustainable Practices
                  </li>
                </ul>
                {/* <a
                    href="/assets/pdfs/virgo-bronze.pdf"
                    download
                    className="mt-auto w-full rounded-lg border-2 border-orange-200 p-4 flex items-center justify-between hover:bg-orange-50 transition-colors cursor-pointer bg-orange-50/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 border border-orange-200">
                        <FileText size={18} />
                      </div>
                      <div>
                        <h4 className="text-card-title font-bold text-gray-900">
                          ZED Bronze Certificate
                        </h4>
                        <p className="text-body text-gray-500 mt-0.5 font-medium">
                          PDF Download
                        </p>
                      </div>
                    </div>
                    <Download size={16} className="text-orange-600" />
                  </a> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. Director's Message */}
      {/* <ScrollReveal delay={0.1}>
        <section className="bg-black text-white px-6 lg:px-12 py-12 md:py-20 mb-20">
          <div className="">
            <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 lg:gap-16">
              <div className="lg:w-[40%]">
                <div className="h-[300px] sm:h-[400px] w-full rounded-lg overflow-hidden relative">
                  <img
                    src="/assets/images/director-message.webp"
                    alt="Office"
                    className="w-full h-full object-cover opacity-80"
                  />
                </div>
              </div>
              <div className="lg:w-[60%] flex flex-col justify-center">
                <div className="space-y-4">
                  <h2 className="text-[10px] uppercase font-light !text-gray-400 font-open-sans-hebrew font-bold">
                    Director’s Message
                  </h2>
                  <h1 className="text-3xl md:text-heading-section font-open-sans-hebrew font-bold bg-gradient-to-r from-croma-blue to-white  bg-clip-text text-transparent">
                    From the Director’s Desk
                  </h1>
                  <div>
                    <p className="text-body font-inter leading-relaxed">
                      My journey in the plywood manufacturing industry began in
                      1985, with the establishment of my first unit in Punjab.
                      Over the years, I have been deeply involved in advancing
                      industry standards through leadership roles in national
                      and state plywood associations, representing the interests
                      of numerous manufacturing units and contributing to the
                      sector’s growth and governance.
                    </p>
                    <br />
                    <p className="text-body font-inter leading-relaxed">
                      Through exposure to global exhibitions and continuous
                      engagement with industry research and training
                      initiatives, my focus has remained on quality, innovation,
                      and modern manufacturing. With Virgo Panel Product, we are
                      building an ultra-modern plywood manufacturing facility in
                      Hoshiarpur, with a clear vision to strengthen Punjab’s
                      position as a leading center for plywood and panel
                      manufacturing in India.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal> */}

      {/* Contact modal - phone numbers from Contact page & Footer */}
      {contactModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setContactModalOpen(false)}
          aria-modal="true"
          role="dialog"
          aria-label="Contact numbers"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setContactModalOpen(false)}
              className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h3 className="text-lg font-open-sans-hebrew font-bold text-gray-900 mb-4 pr-8">
              Contact us
            </h3>
            <p className="text-body text-gray-500 mb-4 font-raleway text-sm">
              Click a number to call
            </p>
            <div className="space-y-3">
              {CONTACT_PHONES.map((item, i) => (
                <a
                  key={i}
                  href={`tel:${item.tel}`}
                  className="flex items-center gap-3 w-full p-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-[#005d9c] transition-colors font-raleway group"
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-[#005d9c] group-hover:bg-[#005d9c] group-hover:text-white transition-colors shrink-0">
                    <Phone size={18} />
                  </span>
                  <div className="min-w-0 flex-1 text-left">
                    <span className="text-xs text-gray-500 block font-raleway">{item.label}</span>
                    <span className="text-body font-medium text-gray-900 font-raleway">{item.number}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
